import React, { useEffect, useMemo, useState } from 'react';
import { taskCompletionService } from '@/firebase/taskCompletionService';
import { checklistCompletionService } from '@/firebase/checklistCompletionService';
import { agreementCompletionService } from '@/firebase/agreementCompletionService';
import { locationService, Location } from '@/firebase/locationService';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useLocationFilter } from '@/contexts/LocationFilterContext';
import { Search, Filter, Paperclip, MapPin, FileText, CheckSquare, FileCheck, FileSignature, Calendar, X, Eye, CheckCircle, Circle, Image, Sparkles, Package, Loader2, Download } from 'lucide-react';
import { trackEvidence } from '@/utils/analytics';
import AuditExportModal from './AuditExportModal';
import { checklistService, Checklist } from '@/firebase/checklistService';
import { agreementService } from '@/firebase/agreementService';
import { complianceService } from '@/firebase/complianceService';
import { 
  fetchUserRoles, 
  taskMatchesUserOwnerRoles,
  getUserOwnerRolesForOrganization,
  getUserSystemRoleForOrganization,
  isManagerInOrganization,
  isAdminInOrganization
} from '@/utils/roleHelpers';
import { getTasksForOrganization } from '@/services/taskService';
import { db } from '@/firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { parseDocument, isGeminiConfigured } from '@/services/geminiService';
import { useNotification } from '@/contexts/NotificationContext';
import { formatTimeDisplay } from '@/utils/checklistTimeTracker';

// Unified document interface for all types
interface UnifiedDocumentFile {
  name: string;
  url: string;
  type?: string;
}

interface UnifiedDocument {
  id: string;
  type: 'document' | 'checklist' | 'agreement';
  name: string; // Template/Form/Checklist/Agreement name
  locationId: string;
  locationName: string;
  completedDate: any; // Date when completed/signed
  completedBy?: string; // User ID
  completedByName?: string; // Formatted display name (e.g., "Name (email)")
  // Files/documents
  files: Array<UnifiedDocumentFile>;
  // Additional metadata
  metadata?: Record<string, any>;
}

const EvidenceVault: React.FC = () => {
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const { selectedLocationIds, hasActiveFilters, availableLocations } = useLocationFilter();
  const [documents, setDocuments] = useState<UnifiedDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'document' | 'agreement'>('all');
  const [allowedLocations, setAllowedLocations] = useState<Location[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<UnifiedDocument | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [checklistTemplate, setChecklistTemplate] = useState<Checklist | null>(null);
  const [taskCompletion, setTaskCompletion] = useState<any>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzingFile, setAnalyzingFile] = useState<string | null>(null);
  const [userOwnerRoles, setUserOwnerRoles] = useState<string[]>([]);
  const [isUserManager, setIsUserManager] = useState<boolean>(false);
  const [taskOwnerRoleMap, setTaskOwnerRoleMap] = useState<Map<string, string>>(new Map());
  const [checklistTemplateMap, setChecklistTemplateMap] = useState<Map<string, string>>(new Map()); // checklistId -> templateId
  const [agreementTemplateMap, setAgreementTemplateMap] = useState<Map<string, string>>(new Map()); // agreementId -> templateId
  const [complianceTemplateMap, setComplianceTemplateMap] = useState<Map<string, string>>(new Map()); // templateId -> owner_role
  const [isAuditExportModalOpen, setIsAuditExportModalOpen] = useState(false);

  const handleDocumentClick = async (doc: UnifiedDocument) => {
    setSelectedDocument(doc);
    setIsLoadingDetails(true);
    
    try {
      if (doc.type === 'checklist' && doc.metadata?.checklistId) {
        const checklistRes = await checklistService.getChecklist(doc.metadata.checklistId);
        if (checklistRes.success && checklistRes.data) {
          setChecklistTemplate(checklistRes.data);
        }
      } else if (doc.type === 'document' && doc.metadata?.taskId) {
        // Load task completion details - find the specific completion that matches this document
        const completionRes = await taskCompletionService.getCompletionsForTask(doc.metadata.taskId);
        if (completionRes.success && completionRes.data && completionRes.data.length > 0) {
          // Try to find the completion that matches this document ID
          const matchingCompletion = completionRes.data.find(c => c.id === doc.id) || completionRes.data[0];
          setTaskCompletion(matchingCompletion);
        }
      }
    } catch (error) {
      console.error('Error loading document details:', error);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  // Function to fetch a file from URL and convert it to a File object
  const urlToFile = async (url: string, fileName: string): Promise<File> => {
    const response = await fetch(url);
    const blob = await response.blob();
    // Determine MIME type from response or file extension
    const mimeType = blob.type || (fileName.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg');
    return new File([blob], fileName, { type: mimeType });
  };

  // Function to analyze evidence files and save results
  const handleAnalyzeEvidence = async () => {
    if (!taskCompletion?.id || !selectedDocument) {
      showNotification('Unable to analyze: Missing completion or document data', 'error');
      return;
    }

    if (!isGeminiConfigured()) {
      showNotification('AI analysis is not configured. Please contact your administrator.', 'error');
      return;
    }

    if (!taskCompletion.evidenceUrls || taskCompletion.evidenceUrls.length === 0) {
      showNotification('No evidence files found to analyze', 'error');
      return;
    }

    setIsAnalyzing(true);
    const documentAnalysesForStorage: Record<string, any> = {};

    try {
      // Get file names from evidenceFileNames or generate default names
      const fileNames = taskCompletion.evidenceFileNames || 
        taskCompletion.evidenceUrls.map((_: string, i: number) => `Evidence ${i + 1}`);

      // Analyze each evidence file
      for (let i = 0; i < taskCompletion.evidenceUrls.length; i++) {
        const url = taskCompletion.evidenceUrls[i];
        const fileName = fileNames[i] || `Evidence ${i + 1}`;
        
        // Skip if URL indicates upload failure
        if (url.startsWith('UPLOAD_FAILED:')) {
          console.warn(`Skipping analysis for failed upload: ${fileName}`);
          continue;
        }

        setAnalyzingFile(fileName);
        
        try {
          // Fetch file from URL and convert to File object
          const file = await urlToFile(url, fileName);
          
          // Only analyze images and PDFs
          if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
            console.warn(`Skipping analysis for unsupported file type: ${file.type}`);
            continue;
          }

          // Run AI analysis
          const analysis = await parseDocument(file);
          
          // Store analysis results
          documentAnalysesForStorage[fileName] = {
            summary: analysis.summary,
            expiryDate: analysis.expiryDate ? analysis.expiryDate.toISOString() : null,
            extractedDates: analysis.extractedDates.map(d => ({
              type: d.type,
              date: d.date.toISOString(),
              confidence: d.confidence,
            })),
            documentType: analysis.documentType,
            keyInformation: analysis.keyInformation,
          };

          console.log(`✅ Analysis completed for ${fileName}`);
        } catch (error: any) {
          console.error(`❌ Error analyzing ${fileName}:`, error);
          // Store error in analysis so user knows it failed
          documentAnalysesForStorage[fileName] = {
            summary: `Analysis failed: ${error.message || 'Unknown error'}`,
            expiryDate: null,
            extractedDates: [],
            documentType: 'unknown',
            keyInformation: {},
          };
        }
      }

      // Save analysis results to task completion
      if (Object.keys(documentAnalysesForStorage).length > 0) {
        const updateResult = await taskCompletionService.updateCompletion(taskCompletion.id, {
          documentAnalyses: documentAnalysesForStorage,
        });

        if (updateResult.success) {
          // Update local state
          setTaskCompletion((prev: any) => ({
            ...prev,
            documentAnalyses: documentAnalysesForStorage,
          }));
          
          // Update selected document metadata
          setSelectedDocument((prev: any) => ({
            ...prev,
            metadata: {
              ...prev?.metadata,
              documentAnalyses: documentAnalysesForStorage,
            },
          }));

          showNotification(`AI analysis completed for ${Object.keys(documentAnalysesForStorage).length} file(s)`, 'success');
        } else {
          showNotification(`Failed to save analysis results: ${updateResult.error}`, 'error');
        }
      } else {
          showNotification('No files were analyzed. Please check file types and try again.', 'info');
      }
    } catch (error: any) {
      console.error('Error during analysis:', error);
      showNotification(`Analysis failed: ${error.message || 'Unknown error'}`, 'error');
    } finally {
      setIsAnalyzing(false);
      setAnalyzingFile(null);
    }
  };

  useEffect(() => {
    const load = async () => {
      // Don't load if organization or user is not available yet
      if (!currentOrganization?.id || !user?.id) {
        console.log('[EvidenceVault] Skipping load - waiting for organization or user:', {
          hasOrganization: !!currentOrganization?.id,
          hasUser: !!user?.id
        });
        return;
      }

      setIsLoading(true);
      const allDocuments: UnifiedDocument[] = [];

      // Load user roles and build task owner role map
      let userOwnerRolesList: string[] = [];
      let isManager = false;
      const taskMap = new Map<string, string>();
      const checklistMap = new Map<string, string>(); // checklistId -> templateId
      const agreementMap = new Map<string, string>(); // agreementId -> templateId
      const templateRoleMap = new Map<string, string>(); // templateId -> owner_role
      
      if (currentOrganization?.id && user?.id) {
        // Load user roles with organization-specific checks
        console.log('[EvidenceVault] Loading user roles for user:', user.id);
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('@/firebase/config');
          const userRef = doc(db, 'users', user.id);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists()) {
            const userData = userSnap.data();
            const organizationId = currentOrganization.id;
            
            // Use organization-specific role helpers
            userOwnerRolesList = getUserOwnerRolesForOrganization(userData, organizationId);
            isManager = isManagerInOrganization(userData, organizationId) || isAdminInOrganization(userData, organizationId);
            
            setUserOwnerRoles(userOwnerRolesList);
            setIsUserManager(isManager);
            
            console.log('[EvidenceVault] User roles loaded (organization-specific):', {
              userId: user.id,
              userEmail: user.email,
              organizationId: organizationId,
              ownerRoles: userOwnerRolesList,
              isManager: isManager,
              isAdmin: isAdminInOrganization(userData, organizationId)
            });
          } else {
            // Fallback to legacy fetchUserRoles if user not found
            const roles = await fetchUserRoles(user.id);
            userOwnerRolesList = roles.ownerRoles;
            isManager = roles.isSystemManager;
            setUserOwnerRoles(userOwnerRolesList);
            setIsUserManager(isManager);
          }
        } catch (error) {
          console.error('[EvidenceVault] Error loading user roles:', error);
          // Fallback to legacy fetchUserRoles on error
          const roles = await fetchUserRoles(user.id);
          userOwnerRolesList = roles.ownerRoles;
          isManager = roles.isSystemManager;
          setUserOwnerRoles(userOwnerRolesList);
          setIsUserManager(isManager);
        }
        
        // OPTIMIZATION: Load all metadata in parallel instead of sequentially
        console.log('[EvidenceVault] Loading metadata in parallel...');
        const [
          allTasksResult,
          checklistsResult,
          agreementsResult,
          templatesResult
        ] = await Promise.allSettled([
          getTasksForOrganization(currentOrganization.id).catch(err => {
            console.error('Error loading tasks for role filtering:', err);
            return [];
          }),
          checklistService.getAllChecklists(currentOrganization.id).catch(err => {
            console.error('Error loading checklists for role filtering:', err);
            return { success: false, data: [] };
          }),
          agreementService.getAllDocuments(currentOrganization.id).catch(err => {
            console.error('Error loading agreements for role filtering:', err);
            return { success: false, data: [] };
          }),
          complianceService.getAllTemplates(currentOrganization.id).catch(err => {
            console.error('Error loading compliance templates for role filtering:', err);
            return { success: false, data: [] };
          })
        ]);
        
        // Process tasks
        if (allTasksResult.status === 'fulfilled') {
          allTasksResult.value.forEach((task: any) => {
            const ownerRole = task.templateSnapshot?.owner_role || task.ownerRole;
            if (ownerRole && task.id) {
              taskMap.set(task.id, ownerRole);
            }
          });
          setTaskOwnerRoleMap(taskMap);
        }
        
        // Process checklists
        if (checklistsResult.status === 'fulfilled' && checklistsResult.value.success && checklistsResult.value.data) {
          checklistsResult.value.data.forEach((checklist: any) => {
            if (checklist.id && checklist.templateId) {
              checklistMap.set(checklist.id, checklist.templateId);
            }
          });
          setChecklistTemplateMap(checklistMap);
        }
        
        // Process agreements
        if (agreementsResult.status === 'fulfilled' && agreementsResult.value.success && agreementsResult.value.data) {
          agreementsResult.value.data.forEach((agreement: any) => {
            if (agreement.id && agreement.templateId) {
              agreementMap.set(agreement.id, agreement.templateId);
            }
          });
          setAgreementTemplateMap(agreementMap);
        }
        
        // Process compliance templates
        if (templatesResult.status === 'fulfilled' && templatesResult.value.success && templatesResult.value.data) {
          templatesResult.value.data.forEach((template: any) => {
            if (template.id && template.owner_role) {
              templateRoleMap.set(template.id, template.owner_role);
            }
          });
          setComplianceTemplateMap(templateRoleMap);
        }
        
        console.log('[EvidenceVault] Metadata loaded:', {
          tasks: taskMap.size,
          checklists: checklistMap.size,
          agreements: agreementMap.size,
          templates: templateRoleMap.size,
          templateIds: Array.from(templateRoleMap.keys()).slice(0, 20) // Show first 20 template IDs for debugging
        });
      }

      // Load accessible locations (filtered by user's assigned locations)
      let userLocationIds: string[] | undefined;
      let userRole: string | undefined;
      let locRes: { success: boolean; data?: Location[] } | null = null;
      let allowedIds = new Set<string>();
      let allowedNames = new Set<string>();
      
      // Load locations first (required for filtering task completions)
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          userLocationIds = userData.locationIds;
          // Use organization-specific role if available
          if (currentOrganization?.id) {
            userRole = getUserSystemRoleForOrganization(userData, currentOrganization.id) || userData.role;
          } else {
            userRole = userData.role;
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }

      locRes = await locationService.getAccessibleLocations(
        currentOrganization.id,
        user?.id,
        userRole,
        userLocationIds
      );
      if (locRes.success && locRes.data) {
        setAllowedLocations(locRes.data);
        
        // Use the fetched locations for filtering (trim whitespace from IDs and names)
        allowedIds = new Set(locRes.data.map(l => (l.id || '').toString().trim()).filter(Boolean) as string[]);
        allowedNames = new Set(locRes.data.map(l => (l.name || '').toString().trim()).filter(Boolean) as string[]);
        
        // Debug: Log allowed location IDs and names
        console.log('[EvidenceVault] Allowed locations:', {
          ids: Array.from(allowedIds),
          names: Array.from(allowedNames)
        });
      } else {
        setAllowedLocations([]);
        console.warn('[EvidenceVault] Failed to load accessible locations');
      }

      // 1. Load Task Completions (Documents/Evidence)
      // Note: taskCompletions don't have organizationId, so we filter by locationIds instead
      // Extract locationIds from allowedLocations to filter at query level
      const locationIdsForQuery = allowedIds.size > 0 ? Array.from(allowedIds) : undefined;
      const taskRes = await taskCompletionService.getAllCompletions(currentOrganization.id, locationIdsForQuery);
      const taskNameMap = new Map<string, string>(); // taskId -> taskName
      const tasksNeedingName: Array<{ completion: any; docIndex: number }> = [];
      
      // Helper function to normalize location names for comparison (trim whitespace, lowercase)
      const normalizeLocationName = (name: string | null | undefined): string => {
        if (!name) return '';
        return (name || '').toString().trim().toLowerCase();
      };
      
      // Create normalized allowed names set for comparison
      const normalizedAllowedNames = new Set(
        Array.from(allowedNames).map(name => normalizeLocationName(name))
      );
      
      console.log('[EvidenceVault] Starting to process task completions:', {
        totalCompletions: taskRes.data?.length || 0,
        organizationId: currentOrganization?.id,
        allowedIds: Array.from(allowedIds),
        allowedNames: Array.from(allowedNames),
        normalizedAllowedNames: Array.from(normalizedAllowedNames)
      });
      
      if (taskRes.success && taskRes.data) {
        let processedCount = 0;
        let rejectedCount = 0;
        let orgMismatchCount = 0;
        let locationFilteredCount = 0;
        let addedCount = 0;
        
        // First pass: collect all task completions that pass basic filters
        const validCompletions: Array<{ completion: any; docIndex: number }> = [];
        
        taskRes.data.forEach((completion, index) => {
          processedCount++;
          
          // Filter out rejected completions (they shouldn't appear in Evidence Vault)
          if (completion.rejected === true) {
            rejectedCount++;
            console.debug('[EvidenceVault] Task completion rejected:', completion.id);
            return;
          }
          
          // Note: taskCompletions don't have organizationId field
          // Organization filtering is done via locationIds (locations have organizationId)
          // Since we already filtered by locationIds in the query, we don't need to check organizationId here
          
          // Filter by accessible locations
          // Priority 1: Use locationId if available (most reliable)
          // Priority 2: Use locationName only as fallback if locationId is missing or doesn't match
          const completionLocationId = (completion.locationId || '').toString().trim();
          const completionLocationName = (completion.locationName || '').toString().trim();
          
          // Primary check: locationId match
          const hasLocationId = completionLocationId && allowedIds.has(completionLocationId);
          
          // Fallback check: locationName match (only if locationId doesn't match or is missing)
          // Normalize by trimming and lowercasing for comparison
          const normalizedCompletionName = normalizeLocationName(completionLocationName);
          const hasMatchingLocationName = !hasLocationId && // Only use name if locationId doesn't match
                                         normalizedCompletionName && 
                                         normalizedAllowedNames.has(normalizedCompletionName);
          
          const inAllowed = hasLocationId || hasMatchingLocationName;
          
          if (!inAllowed) {
            locationFilteredCount++;
            // Debug logging for filtered out completions (only log for current organization)
            if (!currentOrganization?.id || !completion.organizationId || completion.organizationId === currentOrganization.id) {
              // Enhanced logging - especially log if location name contains "Hyatt" or "SLC" to help debug
              const isRelevantLocation = completionLocationName.toLowerCase().includes('hyatt') || 
                                        completionLocationName.toLowerCase().includes('slc') ||
                                        completionLocationName.toLowerCase().includes('airport');
              
              if (isRelevantLocation) {
                console.warn('[EvidenceVault] ⚠️ RELEVANT LOCATION FILTERED OUT:', {
                  id: completion.id,
                  taskId: completion.taskId,
                  templateName: completion.templateName,
                  locationId: completionLocationId,
                  locationName: completionLocationName,
                  rawLocationId: completion.locationId,
                  rawLocationName: completion.locationName,
                  organizationId: completion.organizationId,
                  hasLocationId,
                  hasMatchingLocationName,
                  allowedIds: Array.from(allowedIds),
                  allowedNames: Array.from(allowedNames),
                  normalizedCompletionName,
                  normalizedAllowedNames: Array.from(normalizedAllowedNames),
                  locationIdInAllowed: completionLocationId ? allowedIds.has(completionLocationId) : false,
                  nameInAllowed: normalizedCompletionName ? normalizedAllowedNames.has(normalizedCompletionName) : false,
                  owner_role: completion.owner_role,
                  hasEvidence: !!(completion.evidenceUrls && completion.evidenceUrls.length > 0),
                  evidenceCount: completion.evidenceUrls?.length || 0,
                  evidenceFileNames: completion.evidenceFileNames || []
                });
              } else {
                console.log('[EvidenceVault] ❌ FILTERED OUT by location - task completion:', {
                  id: completion.id,
                  taskId: completion.taskId,
                  templateName: completion.templateName,
                  locationId: completionLocationId,
                  locationName: completionLocationName,
                  rawLocationId: completion.locationId,
                  rawLocationName: completion.locationName,
                  organizationId: completion.organizationId,
                  hasLocationId,
                  hasMatchingLocationName,
                  allowedIds: Array.from(allowedIds),
                  allowedNames: Array.from(allowedNames),
                  normalizedCompletionName,
                  normalizedAllowedNames: Array.from(normalizedAllowedNames),
                  locationIdInAllowed: completionLocationId ? allowedIds.has(completionLocationId) : false,
                  nameInAllowed: normalizedCompletionName ? normalizedAllowedNames.has(normalizedCompletionName) : false,
                  owner_role: completion.owner_role,
                  hasEvidence: !!(completion.evidenceUrls && completion.evidenceUrls.length > 0)
                });
              }
            }
            return;
          }
          
          // Location check passed
          console.debug('[EvidenceVault] ✅ Location check passed for task completion:', {
            id: completion.id,
            locationId: completionLocationId,
            locationName: completionLocationName,
            owner_role: completion.owner_role
          });

          const files: UnifiedDocumentFile[] = [];
          if (completion.evidenceUrls && completion.evidenceUrls.length > 0) {
            completion.evidenceUrls.forEach((url, i) => {
              files.push({
                name: completion.evidenceFileNames?.[i] || `Evidence ${i + 1}`,
                url: url,
                type: url.toLowerCase().endsWith('.pdf') ? 'pdf' : 'image'
              });
            });
          }

          // Check if templateName is missing or looks like a template ID (starts with "Template " followed by alphanumeric)
          const templateName = completion.templateName || '';
          const looksLikeTemplateId = templateName.startsWith('Template ') && /^Template [a-zA-Z0-9]+$/.test(templateName);
          const needsTaskName = !templateName || looksLikeTemplateId;
          
          if (needsTaskName && completion.taskId) {
            tasksNeedingName.push({ completion, docIndex: allDocuments.length });
          }

          // Resolve locationName from locationId if missing
          let resolvedLocationName = completion.locationName || '';
          if (!resolvedLocationName && completion.locationId && locRes?.data) {
            const location = locRes.data.find(l => l.id === completion.locationId);
            if (location) {
              resolvedLocationName = location.name || '';
            }
          }
          if (!resolvedLocationName) {
            resolvedLocationName = 'Unknown';
          }

          // Store completion for second pass (filtering custom tasks)
          validCompletions.push({
            completion: {
              ...completion,
              templateName,
              resolvedLocationName,
              files
            },
            docIndex: allDocuments.length
          });
        });
        
        // Second pass: Fetch task names for display (but don't filter by templateId)
        // OPTIMIZATION: Batch fetch all tasks at once instead of individual fetches
        const uniqueTaskIds = Array.from(new Set(
          validCompletions
            .map(({ completion }) => completion.taskId)
            .filter(Boolean) as string[]
        ));
        
        const taskDataMap = new Map<string, { templateId?: string; name?: string }>();
        
        if (uniqueTaskIds.length > 0) {
          try {
            // Batch fetch all tasks using Promise.all
            const taskPromises = uniqueTaskIds.map(async (taskId) => {
              try {
                const taskRef = doc(db, 'tasks', taskId);
                const taskDoc = await getDoc(taskRef);
                if (taskDoc.exists()) {
                  const taskData = taskDoc.data();
                  return {
                    taskId,
                    templateId: taskData.templateId || taskData.originalTemplateId,
                    name: taskData.title || taskData.name || taskData.templateName || 
                          taskData.templateSnapshot?.name || taskData.templateSnapshot?.name_es
                  };
                }
              } catch (err) {
                console.warn(`[EvidenceVault] Error fetching task ${taskId}:`, err);
              }
              return null;
            });
            
            const taskResults = await Promise.all(taskPromises);
            taskResults.forEach(result => {
              if (result) {
                taskDataMap.set(result.taskId, {
                  templateId: result.templateId,
                  name: result.name
                });
              }
            });
            
            console.log(`[EvidenceVault] Batch fetched ${taskDataMap.size} tasks out of ${uniqueTaskIds.length} requested`);
            
            // Log tasks that were found vs not found for debugging
            const notFoundTaskIds = uniqueTaskIds.filter(id => !taskDataMap.has(id));
            if (notFoundTaskIds.length > 0) {
              console.warn(`[EvidenceVault] ⚠️ ${notFoundTaskIds.length} tasks not found in database:`, notFoundTaskIds.slice(0, 10));
            }
            
            // Log sample of found tasks with their templateIds for debugging
            const sampleTasks = Array.from(taskDataMap.entries()).slice(0, 5);
            console.log('[EvidenceVault] Sample of fetched tasks:', sampleTasks.map(([taskId, data]) => ({
              taskId,
              templateId: data.templateId,
              name: data.name
            })));
          } catch (batchErr) {
            console.error('[EvidenceVault] Error batch fetching tasks:', batchErr);
          }
        }
        
        // Now process completions - include ALL task completions regardless of templateId
        for (const { completion, docIndex } of validCompletions) {
          let taskName: string | undefined;
          
          // Try to get task name from fetched task data (for better display)
          if (completion.taskId) {
            const taskData = taskDataMap.get(completion.taskId);
            if (taskData) {
              taskName = taskData.name;
            }
          }
          
          // Include all task completions - use task name if available, otherwise use templateName
          const documentName = taskName || completion.templateName || 'Unknown Compliance';
          
          allDocuments.push({
            id: completion.id || `task-${Date.now()}-${Math.random()}`,
            type: 'document',
            name: documentName,
            locationId: completion.locationId || '',
            locationName: completion.resolvedLocationName,
            completedDate: completion.completionDate,
            completedBy: completion.completedBy,
            files: completion.files,
            metadata: {
              taskId: completion.taskId,
              assetEvidence: completion.assetEvidence || [],
              owner_role: completion.owner_role, // Store owner_role from completion
              documentAnalyses: completion.documentAnalyses || {}, // Store AI analysis results
            }
          });
          
          addedCount++;
        }
        
        console.log('[EvidenceVault] Task completions processing summary:', {
          totalProcessed: processedCount,
          rejected: rejectedCount,
          orgMismatch: orgMismatchCount,
          locationFiltered: locationFilteredCount,
          addedToDocuments: addedCount,
          totalDocumentsAfter: allDocuments.length,
          complianceTemplatesLoaded: templateRoleMap.size,
          tasksFoundInBatch: taskDataMap.size,
          uniqueTaskIdsRequested: uniqueTaskIds.length,
          note: 'All task completions are now included (no templateId filtering)'
        });

        // OPTIMIZATION: Task names are now fetched in batch above, so we can skip this section
        // The taskNameMap is already populated from the batch fetch
        if (tasksNeedingName.length > 0) {
          // Update document names with fetched task names from batch fetch
          tasksNeedingName.forEach(({ completion, docIndex }) => {
            if (completion.taskId && taskDataMap.has(completion.taskId)) {
              const taskData = taskDataMap.get(completion.taskId);
              if (taskData?.name) {
                allDocuments[docIndex].name = taskData.name;
              }
            }
          });
        }
      }

      // 2. Load Checklist Completions - REMOVED: Checklist completions are no longer shown in Evidence Vault
      // Checklist completions have been removed from the Evidence Vault as per requirements

      // 3. Load Agreement Completions
      if (currentOrganization?.id) {
        const agreementRes = await agreementCompletionService.getCompletionsForOrganization(currentOrganization.id);
        if (agreementRes.success && agreementRes.data) {
          agreementRes.data.forEach(completion => {
            // Filter by accessible locations
            const inAllowed = completion.locationId && allowedIds.has(completion.locationId);
            if (!inAllowed) return;

            const files: UnifiedDocumentFile[] = [];
            if (completion.signedPdfUrl) {
              files.push({
                name: `${completion.agreementName} - Signed`,
                url: completion.signedPdfUrl,
                type: 'pdf'
              });
            }
            if (completion.signatureUrl) {
              files.push({
                name: 'Signature',
                url: completion.signatureUrl,
                type: 'image'
              });
            }

            // Look up location name
            const location = locRes?.data?.find(l => l.id === completion.locationId);
            const locationName = location?.name || 'Unknown';

            // For agreements, use clientSignedAt as fallback if signedAt is not valid
            // Store both signedAt and clientSignedAt in metadata for date parsing
            let dateToUse = completion.signedAt;
            if (!completion.signedAt || 
                (completion.signedAt && typeof completion.signedAt === 'object' && completion.signedAt._methodName === 'serverTimestamp')) {
              // If signedAt is a serverTimestamp placeholder, use clientSignedAt if available
              if (completion.clientSignedAt) {
                dateToUse = completion.clientSignedAt;
              }
            }

            allDocuments.push({
              id: completion.id || `agreement-${Date.now()}-${Math.random()}`,
              type: 'agreement',
              name: completion.agreementName || 'Unknown Agreement',
              locationId: completion.locationId || '',
              locationName: locationName,
              completedDate: dateToUse, // Use clientSignedAt if signedAt is placeholder
              completedBy: completion.signerName,
              files: files,
              metadata: { 
                agreementId: completion.agreementId,
                signerName: completion.signerName,
                signerEmail: completion.signerEmail,
                clientSignedAt: completion.clientSignedAt, // Store client timestamp as fallback
                originalSignedAt: completion.signedAt, // Store original for reference
                owner_role: completion.owner_role, // Store owner_role from completion
                // AI-parsed contract information
                contractSummary: completion.contractSummary,
                contractDate: completion.contractDate,
                expiryDate: completion.expiryDate,
                contractKeyInformation: completion.contractKeyInformation
              }
            });
          });
        }
      }

      // Sort all documents by date (newest first)
      allDocuments.sort((a, b) => {
        const getDate = (doc: UnifiedDocument): number => {
          if (!doc.completedDate) return 0;
          if (doc.completedDate instanceof Date) return doc.completedDate.getTime();
          if (doc.completedDate?.toDate) return doc.completedDate.toDate().getTime();
          if (typeof doc.completedDate === 'number') return doc.completedDate;
          if (doc.completedDate?.clientSignedAt) return doc.completedDate.clientSignedAt;
          return new Date(doc.completedDate).getTime();
        };
        return getDate(b) - getDate(a);
      });

      // Filter documents by owner role if user is not manager/admin
      console.log('[EvidenceVault] Starting owner_role filtering:', {
        isManager,
        userOwnerRolesList,
        totalDocumentsBeforeFilter: allDocuments.length,
        userId: user?.id,
        userEmail: user?.email
      });
      
      let filteredDocuments = allDocuments;
      if (!isManager && userOwnerRolesList.length > 0) {
        // Build a map of agreement documents with owner_role for efficient lookup
        const agreementOwnerRoleMap = new Map<string, string>();
        const agreementIdsToFetch = new Set<string>();
        
        // Collect all agreement IDs that need owner_role lookup
        allDocuments.forEach(doc => {
          if (doc.type === 'agreement' && !doc.metadata?.owner_role && doc.metadata?.agreementId) {
            agreementIdsToFetch.add(doc.metadata.agreementId);
          }
        });
        
        // Fetch owner_role for agreements that don't have it in completion metadata
        if (agreementIdsToFetch.size > 0) {
          try {
            const { doc: firestoreDoc, getDoc } = await import('firebase/firestore');
            const { db } = await import('@/firebase/config');
            await Promise.all(Array.from(agreementIdsToFetch).map(async (agreementId) => {
              try {
                const agreementRef = firestoreDoc(db, 'agreements', agreementId);
                const agreementSnap = await getDoc(agreementRef);
                if (agreementSnap.exists()) {
                  const agreementData = agreementSnap.data();
                  if (agreementData.owner_role) {
                    agreementOwnerRoleMap.set(agreementId, agreementData.owner_role);
                  }
                }
              } catch (error) {
                console.warn(`[EvidenceVault] Error fetching agreement ${agreementId} for owner_role:`, error);
              }
            }));
          } catch (error) {
            console.warn(`[EvidenceVault] Error fetching agreement documents:`, error);
          }
        }
        
        let ownerRoleFilteredCount = 0;
        
        filteredDocuments = allDocuments.filter(doc => {
          // Check if document has owner_role in metadata (for task completions)
          if (doc.metadata?.owner_role) {
            const matches = taskMatchesUserOwnerRoles(doc.metadata.owner_role, userOwnerRolesList);
            if (!matches) {
              ownerRoleFilteredCount++;
              console.log(`[EvidenceVault] ❌ FILTERED OUT by owner_role - "${doc.name}":`, {
                docId: doc.id,
                docType: doc.type,
                docLocationId: doc.locationId,
                docLocationName: doc.locationName,
                documentOwnerRole: doc.metadata.owner_role,
                userOwnerRoles: userOwnerRolesList,
                matches: false
              });
            } else {
              console.debug(`[EvidenceVault] ✅ Owner_role check passed - "${doc.name}":`, {
                docId: doc.id,
                docType: doc.type,
                documentOwnerRole: doc.metadata.owner_role,
                userOwnerRoles: userOwnerRolesList
              });
            }
            return matches;
          }
          
          // Check if document is linked to a task (fallback for older documents without owner_role)
          if (doc.metadata?.taskId) {
            const taskOwnerRole = taskMap.get(doc.metadata.taskId);
            if (taskOwnerRole) {
              return taskMatchesUserOwnerRoles(taskOwnerRole, userOwnerRolesList);
            }
            // Task not found in map - exclude for safety
            return false;
          }
          
          // Check if document is a checklist completion
          if (doc.type === 'checklist' && doc.metadata?.checklistId) {
            // First check if owner_role is in metadata (for newer documents)
            if (doc.metadata.owner_role) {
              return taskMatchesUserOwnerRoles(doc.metadata.owner_role, userOwnerRolesList);
            }
            // Fallback: lookup from checklist template
            const checklistTemplateId = checklistMap.get(doc.metadata.checklistId);
            if (checklistTemplateId) {
              const templateOwnerRole = templateRoleMap.get(checklistTemplateId);
              if (templateOwnerRole) {
                return taskMatchesUserOwnerRoles(templateOwnerRole, userOwnerRolesList);
              }
            }
            // Checklist template not found - exclude for safety
            return false;
          }
          
          // Check if document is an agreement completion
          if (doc.type === 'agreement') {
            // First check if owner_role is in metadata (for newer documents)
            if (doc.metadata?.owner_role) {
              const matches = taskMatchesUserOwnerRoles(doc.metadata.owner_role, userOwnerRolesList);
              if (!matches) {
                console.log(`[EvidenceVault] Agreement "${doc.name}" excluded: owner_role="${doc.metadata.owner_role}" doesn't match user roles:`, userOwnerRolesList);
              }
              return matches;
            }
            
            // Fallback: lookup from agreement document if agreementId exists
            if (doc.metadata?.agreementId) {
              // First try to get owner_role from the pre-fetched map
              const agreementOwnerRole = agreementOwnerRoleMap.get(doc.metadata.agreementId);
              if (agreementOwnerRole) {
                const matches = taskMatchesUserOwnerRoles(agreementOwnerRole, userOwnerRolesList);
                if (!matches) {
                  console.log(`[EvidenceVault] Agreement "${doc.name}" excluded: agreement document owner_role="${agreementOwnerRole}" doesn't match user roles:`, userOwnerRolesList);
                }
                return matches;
              }
              
              // If agreement document doesn't have owner_role, try template lookup
              const agreementTemplateId = agreementMap.get(doc.metadata.agreementId);
              if (agreementTemplateId) {
                const templateOwnerRole = templateRoleMap.get(agreementTemplateId);
                if (templateOwnerRole) {
                  const matches = taskMatchesUserOwnerRoles(templateOwnerRole, userOwnerRolesList);
                  if (!matches) {
                    console.log(`[EvidenceVault] Agreement "${doc.name}" excluded: template owner_role="${templateOwnerRole}" doesn't match user roles:`, userOwnerRolesList);
                  }
                  return matches;
                }
              }
            }
            
            // Agreement has no owner_role (neither in metadata, agreement document, nor via template lookup) - exclude for system users
            console.log(`[EvidenceVault] Agreement "${doc.name}" excluded: no owner_role found in completion, agreement document, or template`);
            return false;
          }
          
          // Not linked to task/checklist/form/agreement - exclude for system users (safety)
          return false;
        });
      } else if (!isManager && userOwnerRolesList.length === 0) {
        // User has no owner roles - show no documents
        filteredDocuments = [];
      }
      // Managers/admins: Show all documents (no filtering)

      // Final diagnostic summary
      console.log('[EvidenceVault] ✅ Final loading summary:', {
        totalDocumentsLoaded: allDocuments.length,
        documentsAfterOwnerRoleFilter: filteredDocuments.length,
        ownerRoleFilteredCount: allDocuments.length - filteredDocuments.length,
        isManager,
        userEmail: user?.email,
        organizationId: currentOrganization?.id,
        allowedLocationsCount: allowedLocations.length,
        totalTaskCompletions: taskRes.data?.length || 0
      });

      setDocuments(filteredDocuments);
      setIsLoading(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization?.id, user?.id]);

  const locations = useMemo(() => {
    // Show all accessible locations (not just locations with documents)
    // This allows users to filter by any location they have access to, even if no documents exist yet
    if (allowedLocations && allowedLocations.length > 0) {
      return allowedLocations
        .map(loc => loc.name)
        .filter((name): name is string => !!name && name.trim() !== '')
        .sort();
    }
    // Fallback: if allowedLocations not loaded yet, show locations from documents
    const map = new Map<string, string>();
    documents.forEach(doc => {
      const name = (doc.locationName || '').trim();
      if (!name || name === 'Unknown') return;
      const key = name.toLowerCase();
      if (!map.has(key)) map.set(key, name);
    });
    return Array.from(map.values()).sort();
  }, [allowedLocations, documents]);


  const filtered = documents.filter(doc => {
    // Filter by location - use global filter if active, otherwise use local filter
    let byLoc: boolean;
    if (hasActiveFilters && selectedLocationIds.length > 0) {
      // Use global location filter - filter by locationId
      byLoc = !!(doc.locationId && selectedLocationIds.includes(doc.locationId));
    } else {
      // Use local location filter - filter by locationName
      byLoc = locationFilter === 'all' || doc.locationName === locationFilter;
    }
    
    // Filter by type
    const byType = typeFilter === 'all' || doc.type === typeFilter;
    
    // Enhanced search across all fields
    const bySearch = !search ||
      doc.name.toLowerCase().includes(search.toLowerCase()) ||
      doc.locationName.toLowerCase().includes(search.toLowerCase()) ||
      doc.completedByName?.toLowerCase().includes(search.toLowerCase()) ||
      doc.completedBy?.toLowerCase().includes(search.toLowerCase()) ||
      doc.files.some(f => f.name.toLowerCase().includes(search.toLowerCase())) ||
      (doc.metadata?.signerName && doc.metadata.signerName.toLowerCase().includes(search.toLowerCase())) ||
      (doc.metadata?.signerEmail && doc.metadata.signerEmail.toLowerCase().includes(search.toLowerCase())) ||
      (doc.metadata?.aiSummary && doc.metadata.aiSummary.toLowerCase().includes(search.toLowerCase())) ||
      (doc.metadata?.assetName && doc.metadata.assetName.toLowerCase().includes(search.toLowerCase()));
    
    return byLoc && byType && bySearch;
  });

  return (
    <div className="p-4 max-w-[95vw] mx-auto">
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-semibold text-gray-800">Evidence Vault</h2>
          <button
            onClick={() => setIsAuditExportModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
          >
            <Package className="w-4 h-4" />
            Audit Export
          </button>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-wrap gap-3 items-center mb-4">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search documents, locations, files, signers..."
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-4 h-4 text-gray-600" />
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value as any)} className="px-3 py-2 border border-gray-300 rounded-md text-sm">
              <option value="all">All Types</option>
              <option value="document">Documents</option>
              <option value="agreement">Agreements</option>
            </select>
            {hasActiveFilters ? (
              <div className="flex items-center gap-2 px-3 py-2 text-sm bg-primary-50 border border-primary-300 rounded-md">
                <span className="text-primary-900 font-medium">
                  Filtered: {selectedLocationIds.length} location{selectedLocationIds.length !== 1 ? 's' : ''}
                </span>
                <span className="text-primary-700 text-xs">
                  ({availableLocations
                    .filter(loc => selectedLocationIds.includes(loc.id!))
                    .map(loc => loc.name)
                    .slice(0, 2)
                    .join(', ')}
                  {selectedLocationIds.length > 2 && ` + ${selectedLocationIds.length - 2} more`})
                </span>
              </div>
            ) : (
            <select value={locationFilter} onChange={e => setLocationFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm">
              <option value="all">All Locations</option>
              {locations.map(loc => (<option key={loc} value={loc}>{loc}</option>))}
            </select>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading documents...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-500 border border-gray-200 rounded">
            <p className="text-lg font-medium">No documents found</p>
            <p className="text-sm mt-1">Try adjusting filters or search terms.</p>
          </div>
        ) : (
          <>
            <div className="mb-3 text-sm text-gray-600">
              Showing {filtered.length} of {documents.length} document{filtered.length !== 1 ? 's' : ''}
            </div>
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto border-collapse">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Type</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Name</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Location</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Completed</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">By</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Files/Documents</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((doc, idx) => {
                  const getDate = (): Date | null => {
                    if (!doc.completedDate) return null;
                    
                    // PRIORITY 1: Already a Date object
                    if (doc.completedDate instanceof Date) {
                      return isNaN(doc.completedDate.getTime()) ? null : doc.completedDate;
                    }
                    
                    // PRIORITY 2: Firestore Timestamp with toDate() method
                    if (doc.completedDate?.toDate && typeof doc.completedDate.toDate === 'function') {
                      try {
                        const date = doc.completedDate.toDate();
                        return isNaN(date.getTime()) ? null : date;
                      } catch (e) {
                        console.warn('Error calling toDate() on Firestore Timestamp:', e);
                      }
                    }
                    
                    // PRIORITY 3: Firestore Timestamp with _seconds property
                    if (doc.completedDate && typeof doc.completedDate === 'object' && ('_seconds' in doc.completedDate || 'seconds' in doc.completedDate)) {
                      const seconds = doc.completedDate._seconds || doc.completedDate.seconds || 0;
                      const nanoseconds = doc.completedDate._nanoseconds || doc.completedDate.nanoseconds || 0;
                      const date = new Date(seconds * 1000 + nanoseconds / 1000000);
                      return isNaN(date.getTime()) ? null : date;
                    }
                    
                    // PRIORITY 4: Number (timestamp in milliseconds or seconds)
                    if (typeof doc.completedDate === 'number') {
                      // If number is less than a certain threshold, assume it's in seconds
                      const date = doc.completedDate < 1000000000000 ? new Date(doc.completedDate * 1000) : new Date(doc.completedDate);
                      return isNaN(date.getTime()) ? null : date;
                    }
                    
                    // PRIORITY 5: String
                    if (typeof doc.completedDate === 'string') {
                      const date = new Date(doc.completedDate);
                      return isNaN(date.getTime()) ? null : date;
                    }
                    
                    // PRIORITY 6: Check metadata for clientSignedAt (for agreements - stored separately)
                    if (doc.metadata?.clientSignedAt && typeof doc.metadata.clientSignedAt === 'number') {
                      const date = new Date(doc.metadata.clientSignedAt);
                      return isNaN(date.getTime()) ? null : date;
                    }
                    
                    // PRIORITY 7: Object with clientSignedAt (for agreements - legacy format)
                    if (doc.completedDate?.clientSignedAt) {
                      const date = new Date(doc.completedDate.clientSignedAt);
                      return isNaN(date.getTime()) ? null : date;
                    }
                    
                    // PRIORITY 8: serverTimestamp placeholder
                    if (doc.completedDate && typeof doc.completedDate === 'object' && doc.completedDate._methodName === 'serverTimestamp') {
                      // Try clientSignedAt from metadata if available
                      if (doc.metadata?.clientSignedAt && typeof doc.metadata.clientSignedAt === 'number') {
                        const date = new Date(doc.metadata.clientSignedAt);
                        return isNaN(date.getTime()) ? null : date;
                      }
                      // Can't determine actual date
                      return null;
                    }
                    
                    // Final fallback: try to construct Date
                    try {
                      const date = new Date(doc.completedDate);
                      return isNaN(date.getTime()) ? null : date;
                    } catch (e) {
                      console.warn('Error parsing date:', e, 'Date value:', doc.completedDate);
                      return null;
                    }
                  };
                  
                  const completedDate = getDate();
                  const dateStr = completedDate ? completedDate.toLocaleString() : 'Invalid date';

                  const getTypeIcon = () => {
                    switch (doc.type) {
                      case 'document':
                        return <FileText className="w-4 h-4 text-blue-600" />;
                      case 'checklist':
                        return <CheckSquare className="w-4 h-4 text-green-600" />;
                      case 'agreement':
                        return <FileSignature className="w-4 h-4 text-orange-600" />;
                      default:
                        return <FileText className="w-4 h-4" />;
                    }
                  };

                  const getTypeLabel = () => {
                    switch (doc.type) {
                      case 'document':
                        return 'Document';
                      case 'checklist':
                        return 'Checklist';
                      case 'agreement':
                        return 'Agreement';
                      default:
                        return 'Unknown';
                    }
                  };

                  return (
                    <tr 
                      key={doc.id || idx} 
                      className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 cursor-pointer`}
                      onClick={() => handleDocumentClick(doc)}
                    >
                      <td className="px-3 py-2 border border-gray-200">
                        <div className="flex items-center gap-1.5">
                          {getTypeIcon()}
                          <span className="text-xs text-gray-600">{getTypeLabel()}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 border border-gray-200">
                        <div className="text-sm text-gray-900 font-medium">{doc.name}</div>
                        {doc.metadata?.assetName && (
                          <div className="text-xs text-gray-600 mt-0.5 flex items-center gap-1">
                            <Package className="w-3 h-3" />
                            <span>{doc.metadata.assetName}</span>
                          </div>
                        )}
                        {doc.metadata?.aiSummary && (
                          <div className="text-xs text-gray-500 italic mt-0.5 truncate max-w-xs" title={doc.metadata.aiSummary}>
                            {doc.metadata.aiSummary.substring(0, 50)}...
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 border border-gray-200">
                        <div className="flex items-center gap-1 text-sm text-gray-700">
                          <MapPin className="w-3 h-3" /> {doc.locationName}
                        </div>
                      </td>
                      <td className="px-3 py-2 border border-gray-200 text-sm text-gray-700">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-gray-500" />
                          {dateStr}
                        </div>
                      </td>
                      <td className="px-3 py-2 border border-gray-200 text-sm text-gray-700">
                        {(doc.completedByName || doc.completedBy) || doc.metadata?.signerName || 'Unknown'}
                        {doc.metadata?.signerEmail && (
                          <div className="text-xs text-gray-500">{doc.metadata.signerEmail}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 border border-gray-200">
                        <div className="flex flex-wrap gap-1" onClick={(e) => e.stopPropagation()}>
                          {doc.files.length > 0 ? (
                            doc.files.map((file, i) => (
                              <a 
                                key={i} 
                                href={file.url} 
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800 hover:bg-blue-100"
                                title={file.name}
                                onClick={() => {
                                  // Track file download
                                  trackEvidence.downloaded(doc.id || 'unknown');
                                }}
                              >
                                <Paperclip className="w-3 h-3" /> 
                                <span className="truncate max-w-[120px]">{file.name}</span>
                              </a>
                            ))
                          ) : (
                            <span className="text-xs text-gray-400 flex items-center gap-1">
                              <FileText className="w-3 h-3" /> No files
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      {/* Document Details Panel */}
      {selectedDocument && (
        <div className="fixed inset-y-0 right-0 w-[500px] bg-white shadow-2xl border-l border-gray-200 z-[130] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50">
            <div>
              <h3 className="text-sm font-semibold text-gray-800">
                {selectedDocument.type === 'document' ? 'Document Details' :
                 selectedDocument.type === 'checklist' ? 'Checklist Run Details' :
                 'Agreement Details'}
              </h3>
              <p className="text-xs text-gray-600 mt-0.5">{selectedDocument.name}</p>
            </div>
            <button
              onClick={() => {
                setSelectedDocument(null);
                setChecklistTemplate(null);
                setTaskCompletion(null);
              }}
              className="text-gray-500 hover:text-gray-700 p-1"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {isLoadingDetails ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                <p className="text-gray-600 mt-2 text-xs">Loading details...</p>
              </div>
            ) : (
              <>
                {/* Common Information */}
                <div className="bg-gray-50 border border-gray-200 rounded p-2">
                  <h4 className="text-xs font-semibold text-gray-800 mb-2">General Information</h4>
                  <div className="space-y-1 text-xs">
                    <div>
                      <span className="font-medium text-gray-700">Type:</span>{' '}
                      <span className="text-gray-900 capitalize">{selectedDocument.type}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Location:</span>{' '}
                      <span className="text-gray-900">{selectedDocument.locationName}</span>
                    </div>
                    {selectedDocument.metadata?.assetName && (
                      <div>
                        <span className="font-medium text-gray-700">Asset:</span>{' '}
                        <span className="text-gray-900">{selectedDocument.metadata.assetName}</span>
                      </div>
                    )}
                    <div>
                      <span className="font-medium text-gray-700">Completed By:</span>{' '}
                      <span className="text-gray-900">{(selectedDocument.completedByName || selectedDocument.completedBy) || selectedDocument.metadata?.signerName || 'Unknown'}</span>
                    </div>
                    {selectedDocument.metadata?.signerEmail && (
                      <div>
                        <span className="font-medium text-gray-700">Email:</span>{' '}
                        <span className="text-gray-900">{selectedDocument.metadata.signerEmail}</span>
                      </div>
                    )}
                    {/* Time Tracking Information - only for checklist type documents */}
                    {selectedDocument.type === 'checklist' && (
                      <>
                        {selectedDocument.metadata?.activeTimeSeconds !== undefined && (
                          <div>
                            <span className="font-medium text-gray-700">Active Time:</span>{' '}
                            <span className="text-gray-900">
                              {formatTimeDisplay(Number(selectedDocument.metadata.activeTimeSeconds) || 0)}
                            </span>
                          </div>
                        )}
                        {selectedDocument.metadata?.totalTimeSeconds !== undefined && (
                          <div>
                            <span className="font-medium text-gray-700">Total Time:</span>{' '}
                            <span className="text-gray-900">
                              {formatTimeDisplay(Number(selectedDocument.metadata.totalTimeSeconds) || 0)}
                            </span>
                          </div>
                        )}
                        {selectedDocument.metadata?.pauseCount !== undefined && Number(selectedDocument.metadata.pauseCount) > 0 && (
                          <div>
                            <span className="font-medium text-gray-700">Pauses:</span>{' '}
                            <span className="text-gray-900">{Number(selectedDocument.metadata.pauseCount)}</span>
                          </div>
                        )}
                      </>
                    )}
                    <div>
                      <span className="font-medium text-gray-700">Date:</span>{' '}
                      <span className="text-gray-900">
                        {(() => {
                          const getDate = (): Date | null => {
                            if (!selectedDocument.completedDate) return null;
                            
                            // PRIORITY 1: Already a Date object
                            if (selectedDocument.completedDate instanceof Date) {
                              return isNaN(selectedDocument.completedDate.getTime()) ? null : selectedDocument.completedDate;
                            }
                            
                            // PRIORITY 2: Firestore Timestamp with toDate() method
                            if (selectedDocument.completedDate?.toDate && typeof selectedDocument.completedDate.toDate === 'function') {
                              try {
                                const date = selectedDocument.completedDate.toDate();
                                return isNaN(date.getTime()) ? null : date;
                              } catch (e) {
                                console.warn('Error calling toDate() on Firestore Timestamp:', e);
                              }
                            }
                            
                            // PRIORITY 3: Firestore Timestamp with _seconds property
                            if (selectedDocument.completedDate && typeof selectedDocument.completedDate === 'object' && ('_seconds' in selectedDocument.completedDate || 'seconds' in selectedDocument.completedDate)) {
                              const seconds = selectedDocument.completedDate._seconds || selectedDocument.completedDate.seconds || 0;
                              const nanoseconds = selectedDocument.completedDate._nanoseconds || selectedDocument.completedDate.nanoseconds || 0;
                              const date = new Date(seconds * 1000 + nanoseconds / 1000000);
                              return isNaN(date.getTime()) ? null : date;
                            }
                            
                            // PRIORITY 4: Number (timestamp in milliseconds or seconds)
                            if (typeof selectedDocument.completedDate === 'number') {
                              const date = selectedDocument.completedDate < 1000000000000 ? new Date(selectedDocument.completedDate * 1000) : new Date(selectedDocument.completedDate);
                              return isNaN(date.getTime()) ? null : date;
                            }
                            
                            // PRIORITY 5: String
                            if (typeof selectedDocument.completedDate === 'string') {
                              const date = new Date(selectedDocument.completedDate);
                              return isNaN(date.getTime()) ? null : date;
                            }
                            
                            // PRIORITY 6: Check metadata for clientSignedAt (for agreements - stored separately)
                            if (selectedDocument.metadata?.clientSignedAt && typeof selectedDocument.metadata.clientSignedAt === 'number') {
                              const date = new Date(selectedDocument.metadata.clientSignedAt);
                              return isNaN(date.getTime()) ? null : date;
                            }
                            
                            // PRIORITY 7: Object with clientSignedAt (for agreements - legacy format)
                            if (selectedDocument.completedDate?.clientSignedAt) {
                              const date = new Date(selectedDocument.completedDate.clientSignedAt);
                              return isNaN(date.getTime()) ? null : date;
                            }
                            
                            // PRIORITY 8: serverTimestamp placeholder
                            if (selectedDocument.completedDate && typeof selectedDocument.completedDate === 'object' && selectedDocument.completedDate._methodName === 'serverTimestamp') {
                              // Try clientSignedAt from metadata if available
                              if (selectedDocument.metadata?.clientSignedAt && typeof selectedDocument.metadata.clientSignedAt === 'number') {
                                const date = new Date(selectedDocument.metadata.clientSignedAt);
                                return isNaN(date.getTime()) ? null : date;
                              }
                              return null;
                            }
                            
                            // Final fallback
                            try {
                              const date = new Date(selectedDocument.completedDate);
                              return isNaN(date.getTime()) ? null : date;
                            } catch (e) {
                              console.warn('Error parsing date:', e, 'Date value:', selectedDocument.completedDate);
                              return null;
                            }
                          };
                          const date = getDate();
                          return date ? date.toLocaleString() : 'Invalid date';
                        })()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Document Type Specific Details */}
                {selectedDocument.type === 'document' && taskCompletion && (
                  <div className="space-y-2">
                    <div className="bg-blue-50 border border-blue-200 rounded p-2">
                      <h4 className="text-xs font-semibold text-gray-800 mb-2">Task Completion</h4>
                      {taskCompletion.comments && (
                        <div className="mb-2">
                          <span className="text-xs font-medium text-gray-700">Comments:</span>
                          <p className="text-xs text-gray-900 mt-0.5">{taskCompletion.comments}</p>
                        </div>
                      )}
                      {taskCompletion.notes && (
                        <div>
                          <span className="text-xs font-medium text-gray-700">Notes:</span>
                          <p className="text-xs text-gray-900 mt-0.5">{taskCompletion.notes}</p>
                        </div>
                      )}
                    </div>

                    {/* AI Analysis - Show button if missing, or show results if available */}
                    {!((taskCompletion.documentAnalyses && Object.keys(taskCompletion.documentAnalyses).length > 0) || 
                      (selectedDocument.metadata?.documentAnalyses && Object.keys(selectedDocument.metadata.documentAnalyses).length > 0)) && 
                      taskCompletion.evidenceUrls && taskCompletion.evidenceUrls.length > 0 && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-xs font-semibold text-gray-800 mb-1 flex items-center gap-1">
                              <Sparkles className="w-4 h-4 text-yellow-600" />
                              AI Analysis Not Available
                            </h4>
                            <p className="text-xs text-gray-600">
                              AI analysis was not performed when this task was completed. Click the button to analyze evidence files now.
                            </p>
                          </div>
                          <button
                            onClick={handleAnalyzeEvidence}
                            disabled={isAnalyzing || !isGeminiConfigured()}
                            className="px-3 py-1.5 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            {isAnalyzing ? (
                              <>
                                <Loader2 className="w-3 h-3 animate-spin" />
                                {analyzingFile ? `Analyzing ${analyzingFile}...` : 'Analyzing...'}
                              </>
                            ) : (
                              <>
                                <Sparkles className="w-3 h-3" />
                                Run AI Analysis
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* AI Analysis Summary from documentAnalyses */}
                    {((taskCompletion.documentAnalyses && Object.keys(taskCompletion.documentAnalyses).length > 0) || 
                      (selectedDocument.metadata?.documentAnalyses && Object.keys(selectedDocument.metadata.documentAnalyses).length > 0)) && (
                      <div className="space-y-2">
                        {Object.entries(taskCompletion.documentAnalyses || selectedDocument.metadata?.documentAnalyses || {}).map(([fileName, analysis]: [string, any]) => {
                          const analysisData = analysis || {};
                          if (!analysisData.summary && !analysisData.documentType && !analysisData.keyInformation) {
                            return null;
                          }

                          return (
                            <div key={fileName} className="space-y-2">
                              {/* AI Analysis Summary */}
                              {analysisData.summary && (
                                <div className="bg-purple-50 border border-purple-200 rounded p-3">
                                  <h4 className="text-xs font-semibold text-gray-800 mb-2 flex items-center gap-1">
                                    <Sparkles className="w-4 h-4 text-purple-600" />
                                    AI Analysis Summary
                                    {fileName && (
                                      <span className="text-gray-500 font-normal ml-1">({fileName})</span>
                                    )}
                                  </h4>
                                  <p className="text-xs text-gray-900 leading-relaxed">{analysisData.summary}</p>
                                </div>
                              )}

                              {/* Document Information (AI-Parsed) */}
                              {(analysisData.documentType || analysisData.expiryDate || analysisData.keyInformation) && (
                                <div className="bg-purple-50 border border-purple-200 rounded p-2">
                                  <h4 className="text-xs font-semibold text-gray-800 mb-2 flex items-center gap-1">
                                    <Sparkles className="w-3 h-3 text-purple-600" />
                                    Document Information (AI-Parsed)
                                    {fileName && (
                                      <span className="text-gray-500 font-normal ml-1">({fileName})</span>
                                    )}
                                  </h4>
                                  <div className="space-y-2 text-xs">
                                    {analysisData.documentType && (
                                      <div>
                                        <span className="font-medium text-gray-700">Document Type:</span>
                                        <p className="text-gray-900 mt-0.5">{analysisData.documentType}</p>
                                      </div>
                                    )}
                                    {analysisData.expiryDate && (
                                      <div>
                                        <span className="font-medium text-gray-700">Expiry Date:</span>
                                        <p className={`font-semibold mt-0.5 ${
                                          (() => {
                                            const expiry = typeof analysisData.expiryDate === 'string' 
                                              ? new Date(analysisData.expiryDate)
                                              : analysisData.expiryDate?.toDate?.() || analysisData.expiryDate;
                                            if (!(expiry instanceof Date) || isNaN(expiry.getTime())) return 'text-gray-900';
                                            const now = new Date();
                                            const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                                            if (daysUntilExpiry < 0) return 'text-red-600';
                                            if (daysUntilExpiry < 30) return 'text-orange-600';
                                            return 'text-green-600';
                                          })()
                                        }`}>
                                          {(() => {
                                            const expiry = typeof analysisData.expiryDate === 'string' 
                                              ? new Date(analysisData.expiryDate)
                                              : analysisData.expiryDate?.toDate?.() || analysisData.expiryDate;
                                            if (!(expiry instanceof Date) || isNaN(expiry.getTime())) return 'Not available';
                                            const formatted = expiry.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
                                            const now = new Date();
                                            const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                                            if (daysUntilExpiry < 0) return `${formatted} (Expired ${Math.abs(daysUntilExpiry)} days ago)`;
                                            if (daysUntilExpiry < 30) return `${formatted} (Expires in ${daysUntilExpiry} days)`;
                                            return formatted;
                                          })()}
                                        </p>
                                      </div>
                                    )}
                                    {analysisData.keyInformation && Object.keys(analysisData.keyInformation).length > 0 && (
                                      <div>
                                        <span className="font-medium text-gray-700">Key Information:</span>
                                        <div className="mt-1 space-y-0.5">
                                          {Object.entries(analysisData.keyInformation).map(([key, value]) => {
                                            // Convert value to string if it's an object
                                            let displayValue: string;
                                            if (value === null || value === undefined) {
                                              displayValue = 'N/A';
                                            } else if (typeof value === 'object') {
                                              // If it's an object, stringify it nicely
                                              try {
                                                displayValue = JSON.stringify(value, null, 2);
                                              } catch (e) {
                                                displayValue = String(value);
                                              }
                                            } else {
                                              displayValue = String(value);
                                            }
                                            
                                            return (
                                              <div key={key} className="flex flex-col sm:flex-row gap-1 sm:gap-2">
                                                <span className="text-gray-600 font-medium break-words min-w-0 sm:min-w-[100px] sm:flex-shrink-0">{key}:</span>
                                                <span className="text-gray-900 whitespace-pre-wrap break-words min-w-0">{displayValue}</span>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {selectedDocument.files.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-800 mb-2">Evidence Files</h4>
                        <div className="space-y-1">
                          {selectedDocument.files.map((file, i) => (
                            <a
                              key={i}
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 text-xs text-blue-800"
                              onClick={() => trackEvidence.downloaded(selectedDocument?.id || 'unknown')}
                            >
                              <Paperclip className="w-3 h-3" />
                              <span className="truncate">{file.name}</span>
                              <Eye className="w-3 h-3 ml-auto" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {taskCompletion.assetEvidence && taskCompletion.assetEvidence.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-800 mb-2">Asset Evidence</h4>
                        <div className="space-y-2">
                          {taskCompletion.assetEvidence.map((assetEntry: any) => (
                            <div key={assetEntry.assetId} className="border border-gray-200 rounded p-2 bg-gray-50">
                              <div className="flex items-center gap-2 mb-1">
                                <Package className="w-3.5 h-3.5 text-primary-600" />
                                <span className="text-xs font-semibold text-gray-900">
                                  {assetEntry.assetName || 'Asset'}
                                </span>
                              </div>
                              <div className="space-y-1 ml-5">
                                {assetEntry.evidence?.map((item: any, idx: number) => (
                                  <div key={item.key || idx} className="flex items-center gap-2 text-xs">
                                    <span className="font-medium text-gray-700">
                                      {item.label}{' '}
                                      <span className="text-gray-500">({item.type})</span>
                                    </span>
                                    {item.url ? (
                                      item.url.startsWith('UPLOAD_FAILED:') ? (
                                        <span className="text-red-600">
                                          Upload failed ({item.fileName || 'file'})
                                        </span>
                                      ) : (
                                        <a
                                          href={item.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                        >
                                          <Paperclip className="w-3 h-3" />
                                          <span className="truncate max-w-[180px]">
                                            {item.fileName || 'View Evidence'}
                                          </span>
                                        </a>
                                      )
                                    ) : (
                                      <span className="text-gray-500 italic">No evidence uploaded</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {selectedDocument.type === 'checklist' && checklistTemplate && (
                  <div className="space-y-2">
                    {selectedDocument.metadata?.variableValues && Object.keys(selectedDocument.metadata.variableValues).length > 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded p-2">
                        <h4 className="text-xs font-semibold text-gray-800 mb-1">Variables</h4>
                        <div className="space-y-1">
                          {Object.entries(selectedDocument.metadata.variableValues).map(([key, value]) => (
                            <div key={key} className="text-xs">
                              <span className="font-medium text-gray-700">{key}:</span>{' '}
                              <span className="text-gray-900">{String(value)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <h4 className="text-xs font-semibold text-gray-800 mb-1.5">
                      Checklist Items ({selectedDocument.metadata?.items || selectedDocument.metadata?.itemStatus ? Object.values(selectedDocument.metadata.items || selectedDocument.metadata.itemStatus || {}).filter((v: any) => v).length : 0}/{checklistTemplate.items?.length || 0} completed)
                      </h4>
                      <div className="space-y-1 border border-gray-200 rounded p-2 max-h-[400px] overflow-y-auto">
                        {checklistTemplate.items?.map((item, index) => {
                        // Use items (mobile format) first, fallback to itemStatus for old records
                        const items = selectedDocument.metadata?.items || selectedDocument.metadata?.itemStatus || {};
                        const isCompleted = items[item.text] || false;
                        const taskRefs = (selectedDocument.metadata?.itemTaskReferences as Record<string, string[]>)?.[item.text] || [];
                          return (
                            <div key={index} className="flex items-start gap-2 p-1.5 bg-gray-50 rounded border border-gray-200">
                              <div className="flex-shrink-0 mt-0.5">
                                {isCompleted ? (
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                ) : (
                                  <Circle className="w-4 h-4 text-gray-300" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-xs text-gray-900">{item.text}</div>
                                {isCompleted && taskRefs.length > 0 && (
                                  <div className="mt-1 text-xs text-blue-600 font-medium">
                                    ✓ Completed by creating task{taskRefs.length > 1 ? 's' : ''} ({taskRefs.length} task{taskRefs.length > 1 ? 's' : ''} created)
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {selectedDocument.files.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-800 mb-2">Files</h4>
                        <div className="space-y-1">
                          {selectedDocument.files.map((file, i) => {
                            const isPdf = file.type === 'pdf' || file.url.toLowerCase().endsWith('.pdf');
                            const Icon = isPdf ? FileText : Image;
                            return (
                              <a
                                key={i}
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 p-2 border border-gray-200 rounded hover:border-blue-400 hover:bg-blue-50 transition text-xs text-gray-800"
                                onClick={() => trackEvidence.downloaded(selectedDocument?.id || 'unknown')}
                              >
                                <Icon className="w-4 h-4 text-gray-600" />
                                <span className="truncate">{file.name || (isPdf ? 'PDF Document' : 'File')}</span>
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {selectedDocument.type === 'agreement' && (
                  <div className="space-y-2">
                    {/* AI Summary - Prominent Display */}
                    {selectedDocument.metadata?.contractSummary && (
                      <div className="bg-purple-50 border border-purple-200 rounded p-3">
                        <h4 className="text-xs font-semibold text-gray-800 mb-2 flex items-center gap-1">
                          <Sparkles className="w-4 h-4 text-purple-600" />
                          AI Analysis Summary
                        </h4>
                        <p className="text-xs text-gray-900 leading-relaxed">{selectedDocument.metadata.contractSummary}</p>
                      </div>
                    )}

                    {/* AI-Parsed Contract Information */}
                    {(selectedDocument.metadata?.contractSummary || selectedDocument.metadata?.contractDate || selectedDocument.metadata?.expiryDate || selectedDocument.metadata?.contractKeyInformation) && (
                      <div className="bg-purple-50 border border-purple-200 rounded p-2">
                        <h4 className="text-xs font-semibold text-gray-800 mb-2 flex items-center gap-1">
                          <Sparkles className="w-3 h-3 text-purple-600" />
                          Contract Summary (AI-Parsed)
                        </h4>
                        <div className="space-y-2 text-xs">
                          <div className="grid grid-cols-2 gap-2">
                            {selectedDocument.metadata?.expiryDate && (
                              <div>
                                <span className="font-medium text-gray-700">Expiry Date:</span>
                                <p className={`font-semibold ${
                                  (() => {
                                    const expiry = typeof selectedDocument.metadata.expiryDate === 'string' 
                                      ? new Date(selectedDocument.metadata.expiryDate)
                                      : selectedDocument.metadata.expiryDate?.toDate?.() || selectedDocument.metadata.expiryDate;
                                    if (!(expiry instanceof Date) || isNaN(expiry.getTime())) return 'text-gray-900';
                                    const now = new Date();
                                    const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                                    if (daysUntilExpiry < 0) return 'text-red-600';
                                    if (daysUntilExpiry < 30) return 'text-orange-600';
                                    return 'text-green-600';
                                  })()
                                }`}>
                                  {(() => {
                                    const expiry = typeof selectedDocument.metadata.expiryDate === 'string' 
                                      ? new Date(selectedDocument.metadata.expiryDate)
                                      : selectedDocument.metadata.expiryDate?.toDate?.() || selectedDocument.metadata.expiryDate;
                                    if (!(expiry instanceof Date) || isNaN(expiry.getTime())) return 'Not available';
                                    const formatted = expiry.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
                                    const now = new Date();
                                    const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                                    if (daysUntilExpiry < 0) return `${formatted} (Expired ${Math.abs(daysUntilExpiry)} days ago)`;
                                    if (daysUntilExpiry < 30) return `${formatted} (Expires in ${daysUntilExpiry} days)`;
                                    return formatted;
                                  })()}
                                </p>
                              </div>
                            )}
                          </div>
                          {selectedDocument.metadata?.contractKeyInformation && Object.keys(selectedDocument.metadata.contractKeyInformation).length > 0 && (
                            <div>
                              <span className="font-medium text-gray-700">Key Information:</span>
                              <div className="mt-1 space-y-0.5">
                                {Object.entries(selectedDocument.metadata.contractKeyInformation).map(([key, value]) => {
                                  // Convert value to string if it's an object
                                  let displayValue: string;
                                  if (value === null || value === undefined) {
                                    displayValue = 'N/A';
                                  } else if (typeof value === 'object') {
                                    // If it's an object, stringify it nicely
                                    try {
                                      displayValue = JSON.stringify(value, null, 2);
                                    } catch (e) {
                                      displayValue = String(value);
                                    }
                                  } else {
                                    displayValue = String(value);
                                  }
                                  
                                  return (
                                    <div key={key} className="flex flex-col sm:flex-row gap-1 sm:gap-2">
                                      <span className="text-gray-600 font-medium break-words min-w-0 sm:min-w-[100px] sm:flex-shrink-0">{key}:</span>
                                      <span className="text-gray-900 whitespace-pre-wrap break-words min-w-0">{displayValue}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {selectedDocument.files.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-800 mb-2">Signed Documents</h4>
                        <div className="space-y-2">
                          {selectedDocument.files.map((file, i) => (
                            <a
                              key={i}
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100"
                              onClick={() => trackEvidence.downloaded(selectedDocument?.id || 'unknown')}
                            >
                              <FileText className="w-4 h-4 text-orange-600" />
                              <span className="text-xs text-gray-900 flex-1">{file.name}</span>
                              <Eye className="w-4 h-4 text-gray-500" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Files Section for all types */}
                {selectedDocument.files.length > 0 && selectedDocument.type !== 'checklist' && selectedDocument.type !== 'document' && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-800 mb-2">Files</h4>
                    <div className="space-y-1">
                      {selectedDocument.files.map((file, i) => (
                        <a
                          key={i}
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 text-xs text-blue-800"
                          onClick={() => trackEvidence.downloaded(selectedDocument?.id || 'unknown')}
                        >
                          <Paperclip className="w-3 h-3" />
                          <span className="truncate">{file.name}</span>
                          <Eye className="w-3 h-3 ml-auto" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
      
      {/* Audit Export Modal */}
      <AuditExportModal
        isOpen={isAuditExportModalOpen}
        onClose={() => setIsAuditExportModalOpen(false)}
      />
    </div>
  );
};

export default EvidenceVault;


